import { yupResolver } from "@hookform/resolvers/yup";
import { Box } from "@mui/material";
import {
  ButtonRow,
  Form,
  FormButton,
  FormStep,
  FormSubmit,
  InputField,
  isEmail,
  Modal,
  ModalProps,
  ToggleField,
  useToast,
} from "@synota-io/synota-shared-ui";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { useBluePenguinInviteUser } from "../../../api/hooks/useBluePenguinInviteUser";

const INPUT_TYPE_OPTIONS = [
  { value: "customer_name" as const, label: "Company Name" },
  { value: "bp_customer_id" as const, label: "Customer ID" },
];

const SANDBOX_OPTIONS = [
  { value: false, label: "Production" },
  { value: true, label: "Sandbox" },
];

const DEFAULT_VALUES: BluePenguinInviteFormValues = {
  input_type: INPUT_TYPE_OPTIONS[0],
  contact_email: "",
  contact_name: "",
  customer_name: "",
  bp_customer_id: "",
  use_sandbox: SANDBOX_OPTIONS[0],
};

interface BluePenguinInviteFormValues {
  input_type: { value: "customer_name" | "bp_customer_id"; label: string };
  contact_email: string;
  contact_name: string;
  customer_name: string | undefined;
  bp_customer_id: string | undefined;
  use_sandbox: { value: boolean; label: string };
}

const schema = yup
  .object({
    input_type: yup
      .object({
        value: yup.string().oneOf(["customer_name", "bp_customer_id"] as const),
        label: yup.string(),
      })
      .required(),
    contact_email: yup
      .string()
      .email("Must be a valid email")
      .required("Contact email is required")
      .test({ test: isEmail, message: "Invalid email" }),
    contact_name: yup.string().required("Contact name is required"),
    customer_name: yup.string().optional(),
    bp_customer_id: yup.string().optional(),
    use_sandbox: yup.object({
      value: yup.boolean().required("Sandbox option is required"),
      label: yup.string().required("Sandbox label is required"),
    }),
  })
  .test(
    "customer_name-or-bp_customer_id",
    "Either Company Name or Blue Penguin Customer ID is required",
    (value) => {
      return value.input_type.value === "customer_name"
        ? !!value.customer_name
        : !!value.bp_customer_id;
    },
  );

export const BpInviteFormModal = ({
  open,
  onClose,
  state,
  ...props
}: ModalProps<{ bp_customer_id?: string; sandbox?: boolean }>) => {
  const toast = useToast();

  const { isPending, inviteUser } = useBluePenguinInviteUser();

  const form = useForm<BluePenguinInviteFormValues>({
    resolver: yupResolver<BluePenguinInviteFormValues>(schema as any),
    values: state?.bp_customer_id
      ? {
          ...DEFAULT_VALUES,
          input_type: INPUT_TYPE_OPTIONS[1],
          bp_customer_id: state.bp_customer_id,
          use_sandbox: state.sandbox ? SANDBOX_OPTIONS[1] : SANDBOX_OPTIONS[0],
        }
      : DEFAULT_VALUES,
  });

  const inputType = form.watch("input_type");

  const onSubmit = form.handleSubmit(
    ({ contact_email, contact_name, customer_name, bp_customer_id, use_sandbox }) => {
      inviteUser(
        {
          contact_email,
          contact_name,
          customer_name:
            inputType.value === "customer_name" ? customer_name || undefined : undefined,
          bp_customer_id:
            inputType.value === "bp_customer_id" ? bp_customer_id || undefined : undefined,
          use_sandbox: use_sandbox.value || undefined,
        },
        {
          onSuccess: () => {
            toast.success("Successfully sent Blue Penguin KYC invite");
            form.reset();
            onClose();
          },
        },
      );
    },
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      state={state}
      fullWidth
      maxWidth="sm"
      title="Invite Blue Penguin User"
      preventCloseOnBackdropClick
      {...props}
      actions={
        <ButtonRow width="100%">
          <FormButton
            variant="text"
            color="error"
            onClick={() => {
              onClose();
              form.reset();
            }}
          >
            Cancel
          </FormButton>
          <Box flexGrow={1} display="flex">
            <FormSubmit
              disabled={!form.formState.isDirty || !form.formState.isValid}
              isLoading={isPending}
              form="blue-penguin-invite-form"
              fullWidth
            >
              {inputType.value === "customer_name" ? "Create Invite" : "Reinvite"}
            </FormSubmit>
          </Box>
        </ButtonRow>
      }
    >
      <Form key={0} id="blue-penguin-invite-form" onSubmit={onSubmit}>
        <FormStep>
          <ToggleField
            fullWidth
            size="small"
            header="Input Type"
            name="input_type"
            control={form.control}
            options={INPUT_TYPE_OPTIONS}
            disabled={isPending}
          />
          {inputType.value === "customer_name" ? (
            <InputField
              key="customer_name"
              autoFocus={!state?.bp_customer_id}
              size="small"
              disabled={isPending}
              header="Company Name"
              name="customer_name"
              control={form.control}
              fullWidth
            />
          ) : (
            <InputField
              key="bp_customer_id"
              autoFocus={!!state?.bp_customer_id}
              size="small"
              disabled={isPending}
              header="Blue Penguin Customer ID"
              name="bp_customer_id"
              control={form.control}
              fullWidth
            />
          )}
          <InputField
            autoFocus={!!state?.bp_customer_id}
            size="small"
            disabled={isPending}
            header="Contact Email"
            name="contact_email"
            control={form.control}
            fullWidth
          />
          <InputField
            size="small"
            disabled={isPending}
            header="Contact Name"
            name="contact_name"
            control={form.control}
            fullWidth
          />
          <ToggleField
            fullWidth
            size="small"
            header="Environment"
            name="use_sandbox"
            control={form.control}
            options={SANDBOX_OPTIONS}
            disabled={isPending}
          />
        </FormStep>
      </Form>
    </Modal>
  );
};
